import { createSelector } from "@reduxjs/toolkit";
import { EntityType } from "Model/EntityType";

import { IState } from "Model/State/IState";
import { ICompany } from "Model/Storage/ICompany";
import { IContact } from "Model/Storage/IContact";
import { IItem } from "Model/Storage/IItem";
import { IProject } from "Model/Storage/IProject";
import { isCompany, isContact } from "Utils/IsType";
import { getItemById } from "Utils/Item";
import { isEmpty } from "Utils/Object";
import { walk } from "Utils/ObjectWalker";
import { filterRecord, recordToArray } from "Utils/Record";
import { isNotNullOrWhiteSpace, isNullOrWhiteSpace } from "Utils/String";
import {
  selectActiveCompanyId,
  selectActiveContactId,
  selectActiveProjectId,
  selectIsDetailsOpen,
  selectSelectedProjectIds
} from "./UI";

export const selectActiveItemType = createSelector(
  selectActiveProjectId,
  selectActiveCompanyId,
  selectActiveContactId,
  (selectedProjectId, selectedCompanyId, selectedContactId) => {
    if (isNotNullOrWhiteSpace(selectedContactId)) {
      return EntityType.Contact;
    }

    if (isNotNullOrWhiteSpace(selectedCompanyId)) {
      return EntityType.Company;
    }

    if (isNotNullOrWhiteSpace(selectedProjectId)) {
      return EntityType.Project;
    }

    return null;
  }
);

export const selectActiveProject = createSelector(
  (state: IState) => selectProjectMap(state),
  (state: IState) => selectActiveProjectId(state),
  (projects, projectId) => projects[projectId]
);

export function selectProjectMap(state: IState): Record<string, IProject> {
  return state.projects.map;
}

export function selectProjectList(state: IState): IProject[] {
  if (isEmpty(state.projects.map)) {
    return [];
  }
  return Object.values(state.projects.map);
}

export function selectHasProjects(state: IState): boolean {
  return isEmpty(state.projects.map);
}

export function selectProjectIds(state: IState): string[] {
  const projectMap = selectProjectMap(state);
  if (isEmpty(projectMap)) {
    return [];
  }

  return Object.keys(projectMap);
}

export function selectProjectTotalCount(state: IState): number {
  return state.projects.totalCount ?? 0;
}

export function selectActiveCompany(state: IState): ICompany {
  const project = selectActiveProject(state);
  if (project == null) {
    return undefined;
  }

  const { dataSource } = project;
  const selectedCompanyId = selectActiveCompanyId(state);

  let company: ICompany;

  walk(
    project,
    `companies`,
    (item) => item[`@id`] === selectedCompanyId,
    (item) => {
      if (isCompany(item)) {
        company = item;
        company.dataSource = dataSource;
      }
      return item;
    }
  );

  return company;
}

export function selectActiveContact(state: IState): IContact {
  const company = selectActiveCompany(state);

  if (company == null) {
    return undefined;
  }

  const { dataSource } = company;
  const selectedContactId = selectActiveContactId(state);

  let contact: IContact;

  walk(
    company,
    `contacts`,
    (item) => item[`@id`] === selectedContactId,
    (item) => {
      if (isContact(item)) {
        contact = item;
        contact.dataSource = dataSource;
      }
      return item;
    }
  );

  return contact;
}

export function selectActiveItem(state: IState): IItem {
  const contact = selectActiveContact(state);
  if (contact != null) {
    return contact;
  }

  const company = selectActiveCompany(state);
  if (company != null) {
    return company;
  }

  return selectActiveProject(state);
}

export function selectSortedProjectIds(state: IState): string[] {
  return state.projects.sort.projectIds;
}

export function selectSortedColumns(state: IState): [string, boolean][] {
  return state.projects.sort.columns;
}

export const selectSelectedProjects = createSelector(
  (state: IState) => selectProjectMap(state),
  (state: IState) => selectSelectedProjectIds(state),
  (projectMap, selectedProjectIds) => recordToArray(filterRecord(projectMap, selectedProjectIds))
);

export const selectSelectedOrActiveProjectIds = createSelector(
  (state: IState) => selectIsDetailsOpen(state),
  (state: IState) => selectSelectedProjectIds(state),
  (state: IState) => selectActiveProjectId(state),
  (isDetailsOpen, selectedProjectIds, activeProjectId) => {
    if (!isDetailsOpen) {
      return selectedProjectIds;
    }

    if (isNullOrWhiteSpace(activeProjectId)) {
      return [];
    }

    return [activeProjectId];
  }
);

export const selectSelectedOrActiveProjects = createSelector(
  (state: IState) => selectIsDetailsOpen(state),
  (state: IState) => selectSelectedProjects(state),
  (state: IState) => selectActiveProject(state),
  (isDetailsOpen, selectedProjects, activeProject): IProject[] => {
    if (!isDetailsOpen) {
      return selectedProjects;
    }

    if (activeProject == null) {
      return [];
    }

    return [activeProject];
  }
);

export function selectProjectsHash(state: IState): string {
  return state.projects.hash;
}

export function selectActiveEditItemId(state: IState): string {
  return state.projects.activeEditItemId;
}

export function selectIsEditing(state: IState): boolean {
  return isNotNullOrWhiteSpace(selectActiveEditItemId(state));
}

export const selectActiveEditItem = createSelector(
  selectActiveProject,
  selectActiveEditItemId,
  (project, itemId): IItem => {
    if (project == null || isNullOrWhiteSpace(itemId)) {
      return null;
    }

    return getItemById(project, itemId);
  }
);

export function selectProjectsCount(state: IState) {
  return Object.keys(state.projects?.map)?.length ?? 0;
}

export function selectLastRequestOn(state: IState) {
  return state.projects.lastRequestOn;
}

export function selectLastRequestIdList(state: IState) {
  return state.projects.lastRequestIdList;
}

export function selectIsLoadingExtended(state: IState) {
  return state.projects.isLoadingExtended ?? false;
}
